import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Award, Download, Image as ImageIcon, Loader2, Copy, TrendingUp, Apple, ArrowRight, Sparkles, CheckCircle2, Circle } from "lucide-react";
import { downloadCertificate, downloadCertificateImage, fetchCertificateImage, RESULT_COPY } from "@/components/quiz/quizUtils";

const NextSteps = ({ code }) => (
  <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mt-8" data-testid="quiz-next-steps">
    <h3 className="font-display text-2xl md:text-3xl text-white mb-2">Let’s find out how your child is really growing</h3>
    <p className="text-white/55 text-sm mb-6">Do both, in this order. {code && <>Use your code <strong className="text-[#FFE66D]">{code}</strong> at checkout for ₹299 + GST each.</>}</p>
    <div className="grid md:grid-cols-2 gap-5">
      {[
        { n: "1", to: "/tools/growth", icon: TrendingUp, title: "Growth Assessment", body: "Height and weight percentiles, predicted adult height and the Growth Health Index.", color: "#4ECDC4", id: "growth" },
        { n: "2", to: "/tools/nin-nutriguide", icon: Apple, title: "NIN-NutriGuide", body: "Daily nutrient targets and a regional meal plan built for your child.", color: "#FF6B6B", id: "nutrition" },
      ].map((s) => (
        <Link key={s.id} to={s.to} data-testid={`quiz-next-${s.id}-link`}
          className="group glass-card rounded-3xl p-6 flex gap-4 transition-transform duration-300 hover:-translate-y-1">
          <span className="font-display text-4xl leading-none" style={{ color: s.color }}>{s.n}</span>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1"><s.icon className="w-4 h-4" style={{ color: s.color }} /><h4 className="font-display text-lg text-white">{s.title}</h4></div>
            <p className="text-sm text-white/55">{s.body}</p>
            <span className="inline-flex items-center gap-1 text-sm mt-3" style={{ color: s.color }}>Start now <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" /></span>
          </div>
        </Link>
      ))}
    </div>
  </motion.div>
);

const CertificatePreview = ({ attempt, onDownload, onDownloadImage, busy }) => {
  const [src, setSrc] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let url;
    fetchCertificateImage(attempt).then((u) => { url = u; setSrc(u); }).catch(() => setFailed(true));
    return () => url && window.URL.revokeObjectURL(url);
  }, [attempt]);
  if (failed) return null;
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="mt-8" data-testid="quiz-certificate-preview">
      <h3 className="font-display text-2xl text-white mb-4">Your certificate</h3>
      <div className="relative rounded-2xl p-[2px] bg-gradient-to-br from-[#FFE66D] via-[#4ECDC4] to-[#A855F7] shadow-2xl shadow-[#4ECDC4]/10">
        <div className="rounded-[14px] overflow-hidden bg-[#0A1029] aspect-[297/210]">
          {src ? <img src={src} alt={`Certificate for ${attempt.full_name}`} className="w-full h-full object-contain" data-testid="quiz-certificate-image" />
            : <div className="w-full h-full flex items-center justify-center"><Loader2 className="w-6 h-6 text-[#4ECDC4] animate-spin" /></div>}
        </div>
      </div>
      <div className="mt-4 flex flex-wrap gap-3">
        <button onClick={onDownload} disabled={!!busy} data-testid="quiz-certificate-preview-download-btn"
          className="btn-glass px-5 py-2.5 rounded-full text-white text-sm inline-flex items-center gap-2 disabled:opacity-60">
          {busy === "pdf" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Download as PDF
        </button>
        <button onClick={onDownloadImage} disabled={!!busy} data-testid="quiz-certificate-image-download-btn"
          className="btn-glass px-5 py-2.5 rounded-full text-white text-sm inline-flex items-center gap-2 disabled:opacity-60">
          {busy === "png" ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImageIcon className="w-4 h-4" />} Download as image
        </button>
      </div>
    </motion.div>
  );
};

const CodeCard = ({ code, price, uses }) => {
  const copy = async () => {
    try { await navigator.clipboard.writeText(code); toast.success("Code copied"); } catch { toast.error("Couldn't copy. Please note the code down."); }
  };
  return (
    <div className="rounded-2xl border border-[#FFE66D]/30 bg-[#FFE66D]/[0.06] p-5" data-testid="quiz-personal-code-card">
      <p className="text-xs uppercase tracking-[0.15em] text-[#FFE66D] mb-2">Your personal code</p>
      <div className="flex items-center gap-3 mb-3">
        <code className="font-display text-2xl text-white tracking-wider" data-testid="quiz-personal-code">{code}</code>
        <button onClick={copy} className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-white/70" data-testid="quiz-code-copy-btn" aria-label="Copy code"><Copy className="w-4 h-4" /></button>
      </div>
      <p className="text-sm text-white/65">One Growth Assessment report and one NIN-NutriGuide report at <strong className="text-white">₹{price?.INR ?? 299} + GST</strong> (or ${price?.USD ?? 10} overseas). Use it at checkout.</p>
      {uses && (
        <div className="flex gap-4 mt-3 text-xs text-white/60">
          {[["growth", "Growth"], ["nutrition", "NutriGuide"]].map(([k, l]) => (
            <span key={k} className="inline-flex items-center gap-1.5" data-testid={`quiz-code-use-${k}`}>
              {uses[k] ? <CheckCircle2 className="w-3.5 h-3.5 text-[#4ECDC4]" /> : <Circle className="w-3.5 h-3.5" />} {l} {uses[k] ? "used" : (
                <Link to={`/checkout?code=${code}&tool=${k}`} className="text-[#FFE66D] underline" data-testid={`quiz-code-buy-${k}`}>buy at ₹{price?.INR ?? 299}</Link>
              )}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export const QuizResult = ({ attempt, code, price, uses, showSteps = false }) => {
  const [busy, setBusy] = useState(false);
  const [downloaded, setDownloaded] = useState(showSteps);
  const r = RESULT_COPY[attempt.result] || RESULT_COPY.good;
  const high = attempt.result === "high_performer";

  const runDownload = async (kind) => {
    setBusy(kind);
    try {
      await (kind === "png" ? downloadCertificateImage(attempt) : downloadCertificate(attempt));
      setDownloaded(true);
      toast.success("Certificate downloaded");
    } catch { toast.error("Couldn't download the certificate. Please try again."); }
    finally { setBusy(false); }
  };
  const download = () => runDownload("pdf");
  const downloadImage = () => runDownload("png");

  return (
    <div data-testid="quiz-result">
      <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} className="relative glass-card rounded-3xl p-8 md:p-12 overflow-hidden">
        <div className="absolute -top-28 -right-28 w-80 h-80 rounded-full blur-3xl pointer-events-none" style={{ background: `${r.color}26` }} />
        <div className="relative grid md:grid-cols-[auto_1fr] gap-8 items-center">
          <div className="w-36 h-36 rounded-full flex flex-col items-center justify-center border-2 mx-auto" style={{ borderColor: r.color, background: `${r.color}14` }}>
            <span className="font-display text-5xl text-white" data-testid="quiz-score">{attempt.score}<span className="text-2xl text-white/50">/{attempt.total}</span></span>
            <span className="text-[10px] uppercase tracking-[0.2em] mt-1" style={{ color: r.color }}>Score</span>
          </div>
          <div>
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold mb-3" style={{ color: r.color, background: `${r.color}1f` }} data-testid="quiz-result-badge">
              {high ? <Sparkles className="w-3.5 h-3.5" /> : <Award className="w-3.5 h-3.5" />} {r.label}
            </span>
            <h2 className="font-display text-3xl md:text-4xl text-white leading-tight mb-3" data-testid="quiz-result-title">
              {high ? `Congratulations, ${attempt.full_name.split(" ")[0]}! You're a High Performer.` : `Good effort, ${attempt.full_name.split(" ")[0]}!`}
            </h2>
            <p className="text-white/60 mb-6">
              {high ? "You know a lot about how children grow and what they need to eat. Your certificate is ready." : "You're on the right track. A growth check and a nutrition plan will show exactly where your child stands. Your certificate is ready."}
            </p>
            <div className="flex flex-wrap gap-3">
              <button onClick={download} disabled={!!busy} data-testid="quiz-certificate-download-btn"
                className="btn-coral px-6 py-3 rounded-full font-semibold inline-flex items-center gap-2 disabled:opacity-60">
                {busy === "pdf" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Download certificate (PDF)
              </button>
              <button onClick={downloadImage} disabled={!!busy} data-testid="quiz-certificate-download-image-btn"
                className="btn-glass px-6 py-3 rounded-full font-semibold text-white inline-flex items-center gap-2 disabled:opacity-60">
                {busy === "png" ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImageIcon className="w-4 h-4" />} Download as image
              </button>
            </div>
          </div>
        </div>
        {code && <div className="relative mt-8"><CodeCard code={code} price={price} uses={uses} /></div>}
      </motion.div>
      <CertificatePreview attempt={attempt} onDownload={download} onDownloadImage={downloadImage} busy={busy} />
      {downloaded && <NextSteps code={code} />}
    </div>
  );
};
